import { proxy } from 'valtio';
import { debounce } from 'lodash';
import type { RaciMode, RaciAssignment, RaciPerson, RaciUser } from '../types/raci';
import { api } from '../utils/api';

interface RaciState {
  mode: RaciMode;
  assignments: Record<string, RaciAssignment>;
  users: RaciUser[];
  loading: boolean;
  searchTerm: string;
  filterGroup: string;
  filterAssigned: 'all' | 'assigned' | 'unassigned';
  filterUsage: 'all' | 'used' | 'unused';
}

export const raciStore = proxy<RaciState>({
  mode: 'soa',
  assignments: {},
  users: [],
  loading: true,
  searchTerm: '',
  filterGroup: 'all',
  filterAssigned: 'all',
  filterUsage: 'all',
});

// Debounced save per item — avoids thrashing when assigning multiple people quickly
const debouncedSaves = new Map<string, ReturnType<typeof debounce>>();

function getDebouncedSave(mode: string, itemKey: string) {
  const key = `${mode}:${itemKey}`;
  if (!debouncedSaves.has(key)) {
    debouncedSaves.set(
      key,
      debounce(async () => {
        const assignment = raciStore.assignments[itemKey];
        if (!assignment) return;
        try {
          await api.put(`/raci/${mode}/${encodeURIComponent(itemKey)}`, {
            responsible: assignment.responsible,
            accountable: assignment.accountable,
            consulted: assignment.consulted,
            informed: assignment.informed,
          });
        } catch (e) {
          console.error('Failed to save RACI assignment:', e);
        }
      }, 500),
    );
  }
  return debouncedSaves.get(key)!;
}

function ensureAssignment(itemKey: string): RaciAssignment {
  if (!raciStore.assignments[itemKey]) {
    raciStore.assignments[itemKey] = {
      mode: raciStore.mode,
      itemKey,
      responsible: [],
      accountable: null,
      consulted: [],
      informed: [],
      updatedAt: new Date().toISOString(),
    };
  }
  return raciStore.assignments[itemKey];
}

export const raciActions = {
  setMode: async (mode: RaciMode) => {
    raciStore.mode = mode;
    raciStore.assignments = {};
    raciStore.filterGroup = 'all';
    raciStore.searchTerm = '';
    raciStore.filterAssigned = 'all';
    raciStore.filterUsage = 'all';
    await raciActions.loadAssignments();
  },

  loadAssignments: async () => {
    try {
      raciStore.loading = true;
      const data = (await api.get(`/raci?mode=${raciStore.mode}`)) as Record<
        string,
        RaciAssignment
      >;
      raciStore.assignments = data || {};
    } catch (e) {
      console.error('Failed to load RACI assignments:', e);
    } finally {
      raciStore.loading = false;
    }
  },

  loadUsers: async () => {
    try {
      const data = (await api.get('/users')) as RaciUser[];
      raciStore.users = (data || []).filter((u) => u.isActive);
    } catch (e) {
      console.error('Failed to load users for RACI:', e);
    }
  },

  loadAll: async () => {
    await Promise.allSettled([raciActions.loadAssignments(), raciActions.loadUsers()]);
  },

  setAssignment: (
    itemKey: string,
    role: 'responsible' | 'consulted' | 'informed',
    people: RaciPerson[],
  ) => {
    const assignment = ensureAssignment(itemKey);
    assignment[role] = people;
    assignment.updatedAt = new Date().toISOString();
    getDebouncedSave(raciStore.mode, itemKey)();
  },

  setAccountable: (itemKey: string, person: RaciPerson | null) => {
    const assignment = ensureAssignment(itemKey);
    assignment.accountable = person;
    assignment.updatedAt = new Date().toISOString();
    getDebouncedSave(raciStore.mode, itemKey)();
  },

  clearAssignment: async (itemKey: string) => {
    delete raciStore.assignments[itemKey];
    try {
      await api.del(`/raci/${raciStore.mode}/${encodeURIComponent(itemKey)}`);
    } catch (e) {
      console.error('Failed to delete RACI assignment:', e);
    }
  },

  setSearchTerm: (term: string) => {
    raciStore.searchTerm = term;
  },
  setFilterGroup: (group: string) => {
    raciStore.filterGroup = group;
  },
  setFilterAssigned: (filter: 'all' | 'assigned' | 'unassigned') => {
    raciStore.filterAssigned = filter;
  },
  setFilterUsage: (filter: 'all' | 'used' | 'unused') => {
    raciStore.filterUsage = filter;
  },
};
